import React from 'react';
import PropTypes from 'prop-types';

import Layout from '~/src/components/layout/Layout';
import NavigationItem from '~/src/components/navigation/item/NavigationItem';
import Boomerang from '~/src/components/svg/general/Boomerang';
import MainLogo from '~/src/components/svg/navigation/MainLogo';

import initPolyglot from '~/src/utilities/libs/polyglot';
import {getCategoryUrl} from '~/src/utilities/helpers/link';
import {classNames} from '~/src/utilities/helpers/css';
import {NavigationItemFormat} from '~/src/utilities/constants/formats';
import {withSettingsContext} from '~/src/components/context/SettingsContext';
import {withSitemapContext} from '~/src/components/context/SitemapContext';
import {SiteArea} from '~/src/utilities/constants/navigation';
import {createNavigationTracker} from '~/src/utilities/tracking/gtm';

import css from './NavigationCategory.module.scss';
import i18nStrings from './NavigationCategory.i18n.json';

const getTranslation = initPolyglot(i18nStrings);

class NavigationCategory extends React.Component {
    navigation = React.createRef();
    content = React.createRef();
    list = React.createRef();
    toggle = React.createRef();
    dropdown = React.createRef();
    activeItem = React.createRef();

    isActive = (category) => {
        return category.categoryId === this.props.categoryId || (Array.isArray(category.children) && category.children.some(this.isActive));
    };

    getHref = (category) => category.contentLink || getCategoryUrl(category.fullUrlPath);

    getTarget = (type) => (type === 'external' ? '_blank' : null);

    mapNavigationItem = (category, format) => {
        const isActive = this.isActive(category);
        const trackingLabel = format === NavigationItemFormat.NAVIGATION_ITEM_DROPDOWN ? `${category.name} (mehr)` : category.name;
        return (
            <NavigationItem
                key={category.categoryId || category.name}
                href={this.getHref(category)}
                target={this.getTarget(category.type)}
                label={category.name}
                format={format}
                flags={{isActive}}
                forwardRef={isActive ? this.activeItem : null}
                onClick={createNavigationTracker(2, trackingLabel, category.categoryId)}
            />
        );
    };

    mapCategory = (category) => this.mapNavigationItem(category, NavigationItemFormat.NAVIGATION_ITEM_CATEGORY);

    mapHighlights = (category) => this.mapNavigationItem(category, NavigationItemFormat.NAVIGATION_ITEM_HIGHLIGHTS);

    mapDropdown = (category) => this.mapNavigationItem(category, NavigationItemFormat.NAVIGATION_ITEM_DROPDOWN);

    getDropdown() {
        const top = this.list.current.getBoundingClientRect().top;
        const count = Array.prototype.filter.call(this.list.current.children, (child) => child.getBoundingClientRect().top > top).length;

        const {right: contentRight} = this.content.current.getBoundingClientRect();
        const toggleLeft = this.toggle.current ? this.toggle.current.getBoundingClientRect().left : contentRight;
        const {width: offsetWidth} = this.navigation.current.getBoundingClientRect();
        const right = offsetWidth - contentRight;
        const minWidth = contentRight - toggleLeft;

        return {
            categories: this.state.categories.slice(-1 * count),
            style: {right, minWidth},
            isVisible: count > 0
        };
    }

    updateDropdown = (isActive = this.state.dropdown.isActive) => {
        const dropdown = this.getDropdown();
        dropdown.isActive = isActive;
        this.setState({dropdown});
    };

    onToggleClick = (event) => {
        if (this.state.dropdown.isActive) {
            const clickOnToggle = this.toggle.current && this.toggle.current.contains(event.target);
            const clickInDropdown = this.dropdown.current && this.dropdown.current.contains(event.target);
            const timeout = clickInDropdown ? 20 : 0;

            if (clickOnToggle) {
                event.stopPropagation();
            }

            window.setTimeout(() => this.updateDropdown(false), timeout);

            document.body.removeEventListener('click', this.onToggleClick);
        } else {
            this.updateDropdown(true);

            window.setTimeout(() => document.body.addEventListener('click', this.onToggleClick), 20);
        }
    };

    onWindowResize = () => this.updateDropdown();

    scrollToActiveItem() {
        if (this.content.current && this.activeItem.current) {
            if (window.getComputedStyle(this.content.current).overflowX === 'auto') {
                const contentCenter = this.content.current.offsetWidth / 2;
                const itemCenter = this.activeItem.current.offsetWidth / 2;
                const scrollX = this.activeItem.current.offsetLeft - contentCenter + itemCenter;

                this.content.current.scrollLeft = scrollX;
            }
        }
    }

    constructor(props) {
        super(props);

        const {settings, sitemap} = this.props.context;
        const categories = (() => {
            switch (settings.siteArea) {
                case SiteArea.VIDEO:
                    return sitemap.video;
                case SiteArea.LIFESTYLE:
                    return sitemap.lifestyle;
                default:
                    return sitemap.news;
            }
        })().filter(({visibility}) => visibility.inMenu !== false);

        this.state = {
            categories: categories.filter(({visibility}) => visibility.highlighted !== true),
            highlights: categories.filter(({visibility}) => visibility.highlighted === true),
            dropdown: {
                categories: [],
                isActive: false,
                isVisible: false
            }
        };
    }

    componentDidMount() {
        this.updateDropdown();
        window.addEventListener('resize', this.onWindowResize);

        this.scrollToActiveItem();
    }

    componentDidUpdate(prevProps) {
        if (this.props.categoryId !== prevProps.categoryId) {
            this.scrollToActiveItem();
        }
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.onWindowResize);
    }

    render() {
        const {dropdown} = this.state;
        const {settings} = this.props.context;

        const withDropdown = dropdown.isActive;
        const classNameNavigation = classNames(css, 'navigation', settings.siteArea, {withDropdown, isWebView: settings.isWebView});

        const isActive = dropdown.categories.some(this.isActive);
        const classNameToggle = classNames(css, 'toggle', {isActive});

        return (
            <div ref={this.navigation} className={classNameNavigation} data-testid="NavigationCategory">
                <Layout>
                    {settings.isWebView && (
                        <div className={css.mainLogo}>
                            <MainLogo {...settings} />
                        </div>
                    )}
                    <div ref={this.content} className={css.content}>
                        <div className={css.categories}>
                            <ul ref={this.list} className={css.list}>
                                {this.state.categories.map(this.mapCategory)}
                            </ul>
                            {dropdown.isVisible && (
                                <button ref={this.toggle} className={classNameToggle} onClick={this.onToggleClick}>
                                    {getTranslation('more')}
                                    <Boomerang />
                                </button>
                            )}
                        </div>
                        {this.state.highlights.length > 0 && (
                            <div className={css.highlights}>
                                <ul className={css.list}>{this.state.highlights.map(this.mapHighlights)}</ul>
                            </div>
                        )}
                    </div>
                </Layout>

                {dropdown.isActive && (
                    <div ref={this.dropdown} className={css.dropdown} style={dropdown.style} data-testid="NavigationDropdown">
                        <ul className={css.list}>{dropdown.categories.map(this.mapDropdown)}</ul>
                    </div>
                )}
            </div>
        );
    }
}

if (process.env.NODE_ENV === 'development') {
    NavigationCategory.propTypes = {
        categoryId: PropTypes.number.isRequired
    };
}

export {NavigationCategory};

export default withSitemapContext(withSettingsContext(NavigationCategory));
